import {
  Robot,
  Scales,
  Warning,
  CaretDown,
  CaretRight,
  CheckCircle,
  XCircle,
  Info,
} from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Checkbox } from "components/ui/checkbox";
import { getConfidenceColor } from "../utils";
import type {
  ExtractedAnswer,
  ConditionUserOverride,
} from "../types";
import { ConditionResultTree } from "./ConditionResultTree";
import { ExistingAnswerDetails } from "./ExistingAnswerDetails";
import { DdAnswerInput } from "./DdAnswerInput";

interface DdAnswerCardProps {
  answer: ExtractedAnswer;
  effectiveAnswer: ExtractedAnswer;
  fileId: string;
  isSaved: boolean;
  isSkipped: boolean;
  conditionKey: string;
  expandedConditionDetails: Set<string>;
  expandedExistingAnswers: Set<string>;
  expandedAIDetails: Set<string>;
  onToggleConditionDetails: (key: string) => void;
  onToggleExistingAnswer: (key: string) => void;
  onToggleAIDetails: (key: string) => void;
  onToggleSkipped: (fileId: string, questionId: number) => void;
  onUpdateAnswer: (fileId: string, questionId: number, field: keyof ExtractedAnswer, value: any) => void;
  getEffectiveConditionPassed: (fileId: string, questionId: number, conditionId: string, originalPassed: boolean) => boolean;
  getConditionOverride: (fileId: string, questionId: number, conditionId: string) => ConditionUserOverride | undefined;
  onToggleConditionOverride: (fileId: string, questionId: number, conditionId: string, originalPassed: boolean) => void;
  onUpdateOverrideReason: (fileId: string, questionId: number, conditionId: string, reason: string) => void;
}

export function DdAnswerCard({
  answer,
  effectiveAnswer,
  fileId,
  isSaved,
  isSkipped,
  conditionKey,
  expandedConditionDetails,
  expandedExistingAnswers,
  expandedAIDetails,
  onToggleConditionDetails,
  onToggleExistingAnswer,
  onToggleAIDetails,
  onToggleSkipped,
  onUpdateAnswer,
  getEffectiveConditionPassed,
  getConditionOverride,
  onToggleConditionOverride,
  onUpdateOverrideReason,
}: DdAnswerCardProps) {
  const hasConditionEval = effectiveAnswer.conditionEvaluation?.usedConditions;
  const isConditionSource = effectiveAnswer.answerSource === 'condition_evaluation';

  return (
    <div
      className={`border rounded-md p-2 transition-all ${
        isSkipped
          ? 'bg-neutral-100 border-neutral-300 opacity-60'
          : isConditionSource
            ? 'bg-emerald-50/50 border-emerald-200'
            : 'bg-violet-50/50 border-violet-100'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          {/* Question header with source indicator */}
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className={`text-xs font-medium ${isConditionSource ? 'text-emerald-800' : 'text-violet-800'}`}>
              {effectiveAnswer.questionDisplayName}
            </span>

            {isConditionSource ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700">
                <Scales className="h-3 w-3" weight="duotone" />
                Voorwaarden
              </span>
            ) : effectiveAnswer.answerSource === 'ai_extraction' ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-violet-100 text-violet-700">
                <Robot className="h-3 w-3" weight="duotone" />
                AI
              </span>
            ) : null}

            {effectiveAnswer.aiConfidence > 0 && (
              <span
                className={`px-1 py-0.5 rounded text-[10px] ${getConfidenceColor(effectiveAnswer.aiConfidence)}`}
              >
                {Math.round(effectiveAnswer.aiConfidence * 100)}%
              </span>
            )}

            {isConditionSource && effectiveAnswer.matchedOptionStatusColor && (
              <span
                className="w-3 h-3 rounded-full border border-neutral-300"
                style={{ backgroundColor: effectiveAnswer.matchedOptionStatusColor }}
                title={`Status: ${effectiveAnswer.matchedOptionStatusColor}`}
              />
            )}

            {answer.existingResponse?.hasExisting && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 border border-amber-300">
                      <Warning className="h-3 w-3" weight="fill" />
                      Bestaand
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">
                      Er bestaat al een antwoord voor deze vraag.
                      {answer.existingResponse.selectedOptionLabel && (
                        <span className="block mt-1 font-medium">
                          Huidig: {answer.existingResponse.selectedOptionLabel}
                        </span>
                      )}
                      {answer.existingResponse.freeTextValue && (
                        <span className="block mt-1 font-medium">
                          Huidig: {answer.existingResponse.freeTextValue.substring(0, 50)}...
                        </span>
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Skip question checkbox */}
          {!isSaved && (
            <div className="flex items-center gap-2 mb-2 mt-1">
              <Checkbox
                id={`skip-${fileId}-${answer.questionId}`}
                checked={isSkipped}
                onCheckedChange={() => onToggleSkipped(fileId, answer.questionId)}
                className="h-3.5 w-3.5"
              />
              <label
                htmlFor={`skip-${fileId}-${answer.questionId}`}
                className="text-[10px] text-neutral-500 cursor-pointer select-none"
              >
                Niet relevant voor dit document (overslaan)
              </label>
            </div>
          )}

          {/* Condition evaluation summary */}
          {hasConditionEval && effectiveAnswer.conditionEvaluation && !isSkipped && (
            <div className="mb-2">
              <p className="text-[11px] text-neutral-600 italic">
                📋 {effectiveAnswer.conditionEvaluation.evaluationSummary}
              </p>

              <button
                onClick={() => onToggleConditionDetails(conditionKey)}
                className="mt-1 flex items-center gap-1 text-[10px] text-emerald-700 hover:text-emerald-800 transition-colors"
              >
                {expandedConditionDetails.has(conditionKey) ? (
                  <CaretDown className="h-3 w-3" weight="bold" />
                ) : (
                  <CaretRight className="h-3 w-3" weight="bold" />
                )}
                <Info className="h-3 w-3" weight="duotone" />
                <span>Bekijk evaluatie details</span>
              </button>

              {expandedConditionDetails.has(conditionKey) && (
                <div className="mt-2 p-2 bg-white rounded border border-emerald-100">
                  {effectiveAnswer.conditionEvaluation.fileLogUsed && (
                    <div className="mb-2 pb-2 border-b border-neutral-100">
                      <p className="text-[10px] font-medium text-neutral-600 mb-1">Geëvalueerd bestand:</p>
                      <div className="text-[10px] text-neutral-500 space-y-0.5">
                        {effectiveAnswer.conditionEvaluation.fileLogUsed.labels?.length > 0 && (
                          <div>Labels: {effectiveAnswer.conditionEvaluation.fileLogUsed.labels.join(', ')}</div>
                        )}
                        {effectiveAnswer.conditionEvaluation.fileLogUsed.expirationDate && (
                          <div>Vervaldatum: {new Date(effectiveAnswer.conditionEvaluation.fileLogUsed.expirationDate).toLocaleDateString('nl-BE')}</div>
                        )}
                        {effectiveAnswer.conditionEvaluation.fileLogUsed.status && (
                          <div>Status: {effectiveAnswer.conditionEvaluation.fileLogUsed.status}</div>
                        )}
                      </div>
                    </div>
                  )}

                  <p className="text-[10px] font-medium text-neutral-600 mb-1">Voorwaarde evaluaties:</p>
                  <div className="space-y-2">
                    {effectiveAnswer.conditionEvaluation.optionEvaluations.map((optEval) => (
                      <div
                        key={optEval.optionId}
                        className={`p-1.5 rounded border ${
                          optEval.matched
                            ? 'bg-green-50 border-green-200'
                            : 'bg-neutral-50 border-neutral-200'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          {optEval.matched ? (
                            <CheckCircle className="h-3.5 w-3.5 text-green-600" weight="fill" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 text-neutral-400" weight="fill" />
                          )}
                          <span className={`text-[10px] font-medium ${optEval.matched ? 'text-green-700' : 'text-neutral-600'}`}>
                            {optEval.optionLabel}
                          </span>
                          <span className="text-[9px] text-neutral-400">
                            ({optEval.logic} logica)
                          </span>
                          {optEval.statusColor && (
                            <span
                              className="w-2.5 h-2.5 rounded-full border border-neutral-300"
                              style={{ backgroundColor: optEval.statusColor }}
                            />
                          )}
                        </div>

                        {optEval.conditionResults.length > 0 && (
                          <div className="ml-1 border-l-2 border-neutral-200 pl-2">
                            {optEval.conditionResults.map((condResult) => (
                              <ConditionResultTree
                                key={condResult.conditionId}
                                result={condResult}
                                depth={0}
                                fileId={fileId}
                                questionId={answer.questionId}
                                isSaved={isSaved}
                                expandedAIDetails={expandedAIDetails}
                                onToggleAIDetails={onToggleAIDetails}
                                getEffectiveConditionPassed={getEffectiveConditionPassed}
                                getConditionOverride={getConditionOverride}
                                onToggleConditionOverride={onToggleConditionOverride}
                                onUpdateOverrideReason={onUpdateOverrideReason}
                              />
                            ))}
                          </div>
                        )}

                        {optEval.conditionResults.length === 0 && (
                          <p className="text-[9px] text-neutral-400 italic ml-4">
                            Geen voorwaarden geconfigureerd
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Existing answer details */}
          {answer.existingResponse?.hasExisting && !isSkipped && (
            <ExistingAnswerDetails
              existingResponse={answer.existingResponse}
              conditionKey={conditionKey}
              isExpanded={expandedExistingAnswers.has(conditionKey)}
              onToggleExpand={onToggleExistingAnswer}
            />
          )}

          {/* Answer input */}
          <DdAnswerInput
            effectiveAnswer={effectiveAnswer}
            fileId={fileId}
            isSaved={isSaved}
            isSkipped={isSkipped}
            onUpdateAnswer={onUpdateAnswer}
          />

          {/* AI reasoning */}
          {effectiveAnswer.aiReasoning && !isSkipped && (
            <p className="mt-1 text-[10px] text-neutral-secondary-mid italic">
              💡 {effectiveAnswer.aiReasoning}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
